import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChatStore } from "@/stores/ChatStore";
import { toast } from "sonner";
import type { TClaimStatusInfo } from "@/types/TClaimStatusInfo";

function generateClaimStatusMessage(claimData: TClaimStatusInfo): string {
  const parts: string[] = [
    `Ich habe Ihren Schaden gefunden.`
  ];

  if (claimData.produkt_name) {
    parts.push(`Es handelt sich um einen Schaden zu Ihrer ${claimData.produkt_name} Versicherung.`);
  }

  // Verwende pending information wenn verfügbar, sonst fallback auf schaden_details
  if (claimData.pending && claimData.pending.length > 0) {
    const firstPending = claimData.pending[0];
    parts.push(`Aktueller Status: ${firstPending.description}`);
    if (firstPending.info) {
      parts.push(firstPending.info);
    }
  } else if (claimData.schaden_details?.status) {
    parts.push(`Der aktuelle Status ist: ${claimData.schaden_details.status}.`);
    if (claimData.status_text) {
      parts.push(claimData.status_text);
    }
  }

  parts.push("Die Details finden Sie im eingeblendeten Fenster.");

  return parts.join(" ");
}

export function ClaimStatusDialog() {
  const isOpen = ChatStore(state => state.claimStatusDialogOpen);
  const setClaimStatusInfo = ChatStore(state => state.setClaimStatusInfo);
  const setDialogOpen = ChatStore(state => state.setClaimStatusDialogOpen);

  const [schadennummer, setSchadennummer] = useState("");
  const [plz, setPlz] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setSchadennummer("");
    setPlz("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!schadennummer.trim()) {
      toast.error("Bitte geben Sie die Schadennummer ein");
      return;
    }

    if (!plz.trim()) {
      toast.error("Bitte geben Sie die Postleitzahl ein");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/multiadapter/claim-status", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          schadennummer: schadennummer.trim(),
          plz: plz.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || "Validierung fehlgeschlagen";
        toast.error(errorMessage);
        return;
      }

      const claimData: TClaimStatusInfo = await response.json();

      if (claimData.valid) {
        setClaimStatusInfo(claimData);
        setDialogOpen(false);
        resetForm();
        toast.success("Schadenstatus geladen");

        // Generate avatar response message
        const statusMessage = generateClaimStatusMessage(claimData);

        // Add message to chat history
        ChatStore.getState().addMessage({
          id: crypto.randomUUID(),
          sender: "assistant",
          transcript: statusMessage,
          timestamp: new Date()
        });

        // Append to delta buffer (for avatar speech if available)
        ChatStore.getState().appendDeltaBuffer(statusMessage);
      } else {
        toast.error("Ungültige Schadennummer oder PLZ");
      }
    } catch (error) {
      console.error("Claim status validation error:", error);
      toast.error("Fehler bei der Validierung");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    resetForm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setDialogOpen(open);
      if (!open) resetForm();
    }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Schadenstatus abrufen</DialogTitle>
          <DialogDescription>
            Bitte geben Sie Ihre Schadennummer und Postleitzahl ein.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="schadennummer">Schadennummer</Label>
            <Input
              id="schadennummer"
              placeholder="z.B. S-2024-123456"
              value={schadennummer}
              onChange={(e) => setSchadennummer(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="plz">Postleitzahl</Label>
            <Input
              id="plz"
              placeholder="z.B. 12345"
              value={plz}
              onChange={(e) => setPlz(e.target.value)}
              disabled={isLoading}
              maxLength={5}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Lädt..." : "Status abrufen"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
